import { PointCloudOctree, Potree } from 'potree-core'
import * as THREE from 'three'

import { Injectable } from '@angular/core'

@Injectable({
    providedIn: 'root'
})
export class PointCloudService {
    public pointClouds: PointCloudOctree[] = []
    public potreeInstance: any = new Potree()
    private modelToPointCloudMap = new Map<number, PointCloudOctree>()

    constructor() {
        this.potreeInstance.pointBudget = 20_000_000
    }

    addPointCloud(pointCloudOctree: PointCloudOctree) {
        this.pointClouds.push(pointCloudOctree)
    }

    addPointCloudToMap(pointCloudOctree: PointCloudOctree, modelId: number | string) {
        const normalizedModelId = Number(modelId)
        if (!Number.isNaN(normalizedModelId)) {
            this.modelToPointCloudMap.set(normalizedModelId, pointCloudOctree)
        }
    }

    getPointCloudForModel(modelId: number | string): PointCloudOctree | undefined {
        const normalizedModelId = Number(modelId)
        return Number.isNaN(normalizedModelId) ? undefined : this.modelToPointCloudMap.get(normalizedModelId)
    }

    setPointBudget(isMobile: boolean) {
        if (isMobile) {
            this.potreeInstance.pointBudget = 1_000_000 // Adjust as needed
        } else {
            this.potreeInstance.pointBudget = 20_000_000 // Adjust as needed
        }
    }

    /**
     * Calculates the bounding box for a point cloud, considering both the full cloud and visible nodes
     */
    calculatePointCloudBoundingBox(pointCloud: PointCloudOctree): THREE.Box3 {
        if (pointCloud.updateBoundingBoxes) {
            pointCloud.updateBoundingBoxes()
        }
        pointCloud.updateMatrixWorld(true)

        const cloudBox = pointCloud.getBoundingBoxWorld
            ? pointCloud.getBoundingBoxWorld().clone()
            : pointCloud.boundingBox.clone()

        // Check if we should use visible nodes for better focus
        let visibleNodesBox = new THREE.Box3()
        let useVisibleNodesBox = false

        if (pointCloud.visibleNodes?.length > 10) {
            pointCloud.visibleNodes.forEach(node => {
                if (node.boundingBox && !node.boundingBox.isEmpty()) {
                    const nodeBox = node.boundingBox.clone()
                        .applyMatrix4(pointCloud.matrixWorld)
                    visibleNodesBox.union(nodeBox)
                }
            })

            const visibleSize = visibleNodesBox.getSize(new THREE.Vector3())
            const cloudSize = cloudBox.getSize(new THREE.Vector3())
            useVisibleNodesBox = !visibleNodesBox.isEmpty() &&
                visibleSize.length() > cloudSize.length() * 0.1
        }

        return useVisibleNodesBox ? visibleNodesBox : cloudBox
    }

    /**
     * Calculates the bounding box for a regular 3D object by traversing its geometry
     */
    calculateObjectBoundingBox(object: THREE.Object3D): { box: THREE.Box3, meshCount: number, pointsCount: number } {
        // Store original world matrices
        const originalWorldMatrix = object.matrixWorld.clone()
        const originalNodeMatrices = new Map()

        object.traverse(child => {
            if (child instanceof THREE.Mesh || child.type === 'Points') {
                originalNodeMatrices.set(child, child.matrixWorld.clone())
            }
        })

        object.updateMatrixWorld(true)

        const box = new THREE.Box3()
        let meshCount = 0
        let pointsCount = 0

        object.traverse(child => {
            if (child instanceof THREE.Mesh) {
                meshCount++
                if (child.geometry) {
                    if (child.geometry.boundingBox) {
                        const worldBox = child.geometry.boundingBox.clone()
                        worldBox.applyMatrix4(originalNodeMatrices.get(child) || child.matrixWorld)
                        box.union(worldBox)
                    } else {
                        child.geometry.computeBoundingBox()
                        const worldBox = child.geometry.boundingBox.clone()
                        worldBox.applyMatrix4(originalNodeMatrices.get(child) || child.matrixWorld)
                        box.union(worldBox)
                    }
                }
            } else if (child.type === 'Points') {
                pointsCount++
                const points = child as THREE.Points
                if (points.geometry) {
                    if (points.geometry.boundingBox) {
                        const worldBox = points.geometry.boundingBox.clone()
                        worldBox.applyMatrix4(originalNodeMatrices.get(child) || points.matrixWorld)
                        box.union(worldBox)
                    } else {
                        points.geometry.computeBoundingBox()
                        const worldBox = points.geometry.boundingBox.clone()
                        worldBox.applyMatrix4(originalNodeMatrices.get(child) || points.matrixWorld)
                        box.union(worldBox)
                    }
                }
            }
        })

        // If we didn't find any geometry, use the object's world position
        if (meshCount === 0 && pointsCount === 0) {
            const worldPosition = new THREE.Vector3()
            worldPosition.setFromMatrixPosition(originalWorldMatrix)
            box.expandByPoint(worldPosition)
        }

        // Restore original matrices
        object.matrixWorld.copy(originalWorldMatrix)
        originalNodeMatrices.forEach((matrix, child) => {
            child.matrixWorld.copy(matrix)
        })

        return { box, meshCount, pointsCount }
    }
}
